// Display label maps now flow from /v2/config via useConfigOptions; helpers
// (numberFormatter, dateToHumanFormat) stay local.
import { numberFormatter, dateToHumanFormat } from "components/misc/constants";
import useConfigOptions from "hooks/useConfigOptions";

const pillClass = (val) => {
    switch (val) {
        case "Acceptable":
            return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30";
        case "Satisfactory":
            return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30";
        case "Not Satisfactory":
            return "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30";
        default:
            return "bg-gray-50 text-gray-600 ring-gray-200 dark:bg-white/[.05] dark:text-white/[.7] dark:ring-white/[.1]";
    }
};

const Pill = ({ value }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${pillClass(value)}`}>
        {value}
    </span>
);

const Row = ({ label, children }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-3.5">
        <dt className="text-sm text-gray-500 dark:text-white/[.5]">{label}</dt>
        <dd className="text-sm text-gray-900 dark:text-white/[.8] sm:col-span-2">{children}</dd>
    </div>
);

const Section = ({ title, description, children }) => (
    <section className="mb-10">
        <div className="mb-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white/[.9]">{title}</h3>
            {description && <p className="mt-0.5 text-sm text-gray-500 dark:text-white/[.5]">{description}</p>}
        </div>
        <dl className="divide-y divide-gray-100 dark:divide-white/[.08]">
            {children}
        </dl>
    </section>
);

function ApplicationDetails({ loan }) {
    const {
        relationshipManagersOutput, balanceOptsOutput,
        numOfYearsWithEmployerOutput, sectorOptsOutput,
        missedPaymentOptionsOutput, billsHistoryOptsOutput,
    } = useConfigOptions();

    return (
        <div>
            <Section title="Scoring Information" description="Denotes how we graded your credit-worthiness.">
                <Row label="Credit Score">{loan?.score} %</Row>
                <Row label="Debt to Income Ratio (DTI)">{loan?.DIR} %</Row>
                <Row label="Willingness conclusion"><Pill value={loan?.willingness} /></Row>
                <Row label="Capacity conclusion"><Pill value={loan?.capacity} /></Row>
                <Row label="Preliminary credit conclusion"><Pill value={loan?.creditConc} /></Row>
            </Section>

            <Section title="Loan Information" description="Loan details and application.">
                <Row label="Relationship Manager">{relationshipManagersOutput[loan?.relManager]}</Row>
                <Row label="Amount Requested">GHS {numberFormatter(loan?.amountReq)}</Row>
                <Row label="Loan Amount">GHS {numberFormatter(loan?.amount)}</Row>
                <Row label="Purpose of loan">{loan?.purpose}</Row>
                <Row label="Desired payment plan">{loan?.paymentPlan}</Row>
                <Row label="Loan duration (Term in months)">{loan?.paymentDuration}</Row>
                <Row label="Repayment Method"><span className="capitalize">{loan?.repaySource}</span></Row>
                <Row label="Currently Employed">{loan?.employedCurrently}</Row>
                <Row label="Receive regular monthly income">{loan?.incomeStatus}</Row>
                <Row label="Monthly income equal or greater than GHS2,000">{loan?.salaryStatus}</Row>
                <Row label="Monthly income">GHS {numberFormatter(loan?.income)}</Row>
                <Row label="Monthly debt">GHS {numberFormatter(loan?.debtAmt)}</Row>
                <Row label="Savings account balance">{balanceOptsOutput[loan?.momoBalance]}</Row>
                <Row label="Name of employer">{loan?.employerName}</Row>
                <Row label="Number of years at work">{numOfYearsWithEmployerOutput[loan?.yearsAtWork]}</Row>
                <Row label="Sector of employment">{sectorOptsOutput[loan?.employmentSector]}</Row>
                <Row label="Outstanding debts or loans">{loan?.outstandingLoans}</Row>
                <Row label="Taken a loan from any financial institution">{loan?.loansTaken}</Row>
                <Row label="Ever defaulted or missed a loan payment">{loan?.loanDefaultStatus}</Row>
                <Row label="Number of times you missed payment on your loan">{missedPaymentOptionsOutput[loan?.defaultedLoanStatus]}</Row>
                <Row label="Bills that you are responsible for">{loan?.billsOption}</Row>
                <Row label="Length of history of your bills payment history">{billsHistoryOptsOutput[loan?.billsHistory]}</Row>
                <Row label="Number of times you missed payment on your bills">{missedPaymentOptionsOutput[loan?.billsHistoryStatus]}</Row>
                <Row label="Recent months loan payment history">{loan?.loanPaymentHistory}</Row>
                <Row label="Recent months bills payment history">{loan?.billPaymentHistory}</Row>
                <Row label="Savings Habit">{loan?.savingHabits}</Row>
                <Row label="Insurance Habit">{loan?.typeOfInsurance !== "None" ? `Yes / ${loan?.typeOfInsurance}` : "No"}</Row>
                <Row label="Loan payment automated / Method of automation">{loan?.automatedPaymentsStatus} / {loan?.autoDeduction}</Row>
                <Row label="Guarantor's name">{loan?.guarantorFirstName} {loan?.guarantorLastName}</Row>
                <Row label="Guarantor's Phone Number">{loan?.guarantorPhone}</Row>
                <Row label="Guarantor's Address">{loan?.guarantorAddress}</Row>
                <Row label="Guarantor's Relationship to you">{loan?.guarantorRelationship}</Row>
                <Row label="Guarantor's ID Type and Number">{loan?.guarantorIdType} / <span className="font-medium">{loan?.guarantorIdNumber}</span></Row>
                <Row label="Guarantor's digital address">{loan?.guarantorDigitalAddress}</Row>
                <Row label="Guarantor's date of birth">{dateToHumanFormat(loan?.guarantorDob)}</Row>
            </Section>
        </div>
    )
}

export default ApplicationDetails
